import {useEffect, useRef, useState} from 'react';
import {confirmExternalPayment, ExternalPaymentOrder, ExternalPaymentReceipt, loadExternalPaymentOrders} from './api';

function matchesConfirmedReadback(order: ExternalPaymentOrder, receipt: ExternalPaymentReceipt, items: ExternalPaymentOrder[]) {
  const current = items.find(item => item.customer_organization_id === order.customer_organization_id
    && item.order_id === order.order_id && item.order_version === order.order_version);
  const confirmation = current?.confirmation;
  return confirmation?.confirmation_id === receipt.confirmation_id
    && confirmation.order_version === order.order_version
    && confirmation.confirmation_code === 'payment_handled_externally'
    && confirmation.status === 'confirmed';
}

export function ExternalPaymentConfirmation() {
  const [orders, setOrders] = useState<ExternalPaymentOrder[] | null>(null);
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(false);
  const [focusKey, setFocusKey] = useState(0);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const mutationPending = useRef(false);
  const confirmationKeys = useRef(new Map<string, string>());
  useEffect(() => {if (focusKey > 0) statusRef.current?.focus();}, [focusKey]);

  async function refresh() {setOrders(await loadExternalPaymentOrders());}
  useEffect(() => {refresh().catch(() => setStatus('External payment status is not available.'));}, []);

  async function confirm(order: ExternalPaymentOrder) {
    if (mutationPending.current) return;
    const identity = `${order.order_id}:${order.order_version}`;
    const idempotencyKey = confirmationKeys.current.get(identity) ?? crypto.randomUUID();
    confirmationKeys.current.set(identity, idempotencyKey);
    mutationPending.current = true;
    setPending(true); setStatus('');
    try {
      const receipt = await confirmExternalPayment(order, idempotencyKey);
      const refreshed = await loadExternalPaymentOrders();
      if (!matchesConfirmedReadback(order, receipt, refreshed)) throw new Error('Confirmation readback mismatch.');
      setOrders(refreshed);
      confirmationKeys.current.delete(identity);
      setStatus('Recorded: Carolina Server handled payment externally. ProFenz did not charge the customer.');
    } catch {
      setStatus('Confirmation result could not be confirmed. Try again here using the same request.');
    } finally {
      mutationPending.current = false;
      setPending(false);
      setFocusKey(value => value + 1);
    }
  }

  return <section className="panel" aria-labelledby="external-payment-title" aria-busy={pending}>
    <p className="eyebrow">PILOT OPERATIONS</p><h2 id="external-payment-title">External payment confirmation</h2>
    <p>Carolina Server handles payment separately. ProFenz does not collect cards or process this payment.</p>
    <p ref={statusRef} tabIndex={-1} role="status" className="notice" aria-live="polite">{status || (orders === null ? 'Loading custody-owned orders…' : pending ? 'Recording external payment confirmation…' : '')}</p>
    {orders && <ol className="intake-list">{orders.map(order => <li className="intake-card" key={order.order_id}>
      <h3>{order.confirmation ? 'Payment confirmed' : 'Awaiting external confirmation'}</h3><p>Order {order.order_id} · version {order.order_version}</p>
      {order.confirmation && <p>Confirmed for version {order.confirmation.order_version} at {order.confirmation.confirmed_at} UTC.</p>}
      {!order.confirmation && <button disabled={pending} onClick={() => void confirm(order)}>Confirm Carolina Server handled payment</button>}
    </li>)}</ol>}
  </section>;
}
